import cloneDeep from 'lodash/cloneDeep.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isNull from 'lodash/isNull.js';
import keys from 'lodash/keys.js';
import merge from 'lodash/merge.js';
import toArray from 'lodash/toArray.js';

import CourseTemplateModel from '@/models/CourseTemplate.js';
import Objective from '@/models/Objective.js';
import axios from '@/plugins/axios.js';
import CourseTemplate from '@/store/modules/coursetemplates/CourseTemplate.js';
import Collection from '@/store/quill/Collection.js';
import EdverumTable from '@/store/utilities/EdverumTable.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';

const collectionHelpers = EdverumTable.collection('course_template');

const renderMenu = array => {
    const container = [];

    const hasVisibleAtFields = settings => {
        if (!settings) {
            return false;
        }
        return !isNull(settings.week_visible) && !isNull(settings.day_visible) && !isNull(settings.time_visible);
    };

    array
        .filter(item => item.id)
        .forEach(item => {
            const type = item.type.includes('sec') ? 'section' : 'activity_template';
            const settings = type === 'section' ? 'section_settings_template' : 'activity_settings_template';

            const menuItem = {
                [`${type}_id`]: item.id,
                course_id: item.course_template_id,
                family: [item.uuid],
                id: item.id,
                prerequisite: item.prerequisite,
                text: item.title ? item.title : 'Untitled Menu Item',
                title: null,
                type,
                uuid: item.uuid,
                parent: item.parent,
                order: item.order,
                visible_at: null,
                course_authorized_role_id: null,
            };

            if (type === 'section') {
                menuItem.section_type = item[settings].type;

                menuItem.week_number = item[settings].week_number;

                if (menuItem.section_type === 'week') {
                    menuItem.display_week_number = item[settings].display_week_number;
                }
            } else {
                menuItem.activity_type = item.activity_type;
            }

            if (hasVisibleAtFields(item[settings])) {
                menuItem.visible_at = {
                    week: item[settings].week_visible,
                    day: item[settings].day_visible,
                    time: item[settings].time_visible,
                };
            }

            container.push(menuItem);
        });

    return container;
};

export default merge(Collection('CourseTemplates', CourseTemplate), {
    state: {
        sort: {
            title: 'asc',
            published_at: 'desc',
        },
    },

    getters: {
        ...collectionHelpers.getters,

        mockMenu: (state, getters, rootState, rootGetters) => {
            const search = new RecursiveSearch();
            const menuItems = cloneDeep(rootGetters['CourseTemplateForm/courseItem']('items'));
            const sections = renderMenu(toArray(rootGetters['SectionTemplates/all']()));
            const activities = renderMenu(toArray(rootGetters['ActivityTemplates/all']()));

            const combined = sections.concat(activities);

            search.do(menuItems, 'items', item => {
                keys(item)
                    .filter(key => !['items', 'parent', 'uuid'].includes(key))
                    .forEach(key => {
                        delete item[key];
                    });

                const targetIndex = combined.findIndex(i => i.uuid === item.uuid);

                let addMenuProps = keys(combined[targetIndex]);

                addMenuProps = addMenuProps.filter(item => item !== 'items');

                addMenuProps.forEach(key => {
                    item[key] = combined[targetIndex][key];
                });

                if (combined[targetIndex]) {
                    item = JSON.parse(JSON.stringify(Object.assign(combined[targetIndex], item)));
                }
            });

            menuItems.map(item => {
                if (item.section_type === 'week') {
                    const objective = Objective.query().with('criterias').where('subject_id', item.id).first();

                    if (objective === null || typeof objective.id !== 'number') {
                        return;
                    }

                    if (objective && typeof objective.id === 'number' && item.id === objective.subject_id) {
                        item.items[0] = {
                            text: objective.title ? objective.title : `Week ${item.week_number} Objective`,
                            id: objective.id,
                            type: 'objective',
                        };
                    }
                }
            });

            return menuItems;
        },
    },

    actions: {
        clearSort: collectionHelpers.actions.clearSort,
        paginateAndFetch: collectionHelpers.actions.paginateAndFetch,

        fetch: collectionHelpers.actions.fetch(({ context, queryBuilder }) => {
            const { getters, rootGetters } = context;

            queryBuilder
                .include(['groups', 'courseCodes', 'tags'])
                .sort(getters.queryBuilderSorting)
                .setPagination(getters.paginate);

            if (rootGetters['CourseTemplatesView/hasViewArchive']) {
                queryBuilder.raw({
                    view_archived: rootGetters['CourseTemplatesView/searchViewArchive'],
                });
            }

            if (rootGetters['CourseTemplatesView/hasQuery']) {
                queryBuilder.raw({
                    search: rootGetters['CourseTemplatesView/searchQuery'],
                });
            }

            if (rootGetters['CourseTemplatesView/hasTagSearch']) {
                queryBuilder.raw({
                    tags: rootGetters['CourseTemplatesView/searchTag'],
                });
            }

            if (rootGetters['CourseTemplatesView/hasGroupSearch']) {
                queryBuilder.raw({
                    groups: rootGetters['CourseTemplatesView/searchGroup'],
                });
            }

            if (rootGetters['CourseTemplatesView/hasCourseCodeSearch']) {
                queryBuilder.raw({
                    course_code: rootGetters['CourseTemplatesView/searchCourseCode'],
                });
            }
        }),

        fetchOne: collectionHelpers.actions.fetchOne(
            ({ queryBuilder }) => {
                queryBuilder
                    .include([
                        'activityTemplates.media',
                        'activityTemplates.activitySettingsTemplate',
                        'activityTemplates.rubricTemplate',
                        'groups.automatedSurveys',
                        'sectionTemplates.sectionSettingsTemplate',
                        'sectionTemplates.timePeriodTemplates.satisfierTemplates',
                        'sectionTemplates.timePeriodTemplates.locations',
                        'courseCodes',
                        'tags',
                        'sectionTemplates.objectiveTemplate',
                    ])
                    .raw({ attendance_scheduler_structure: true });
            },
            async ({ data, meta, context }) => {
                if (has(meta, 'attendance_scheduler_structure')) {
                    await context.dispatch(
                        'AttendanceScheduler/loadStructure',
                        get(meta, 'attendance_scheduler_structure'),
                        { root: true }
                    );
                }

                await CourseTemplateModel.insert({ data });

                await context.dispatch('CourseTemplateForm/hydrate', { payload: data }, { root: true });
            }
        ),

        /**
         * @param context
         * @param filter
         * @return {Promise<any>}
         */
        async filterAndFetch(context, filter) {
            try {
                await context.dispatch('filter', filter);
                await context.dispatch('purge');
                await context.dispatch('fetch');
            } catch (error) {
                return error;
            }
        },

        /**
         * @param context
         * @param payload
         * @return {Promise<any>}
         */
        async launchCourse(context, payload) {
            const course = context.rootGetters['CourseTemplateForm/course'];

            const newCourse = {
                course_template_id: course.id,
                description: course.description,
                ...payload,
            };

            const { data } = await axios.post('/api/courses/launch', newCourse, { errorHandle: false });

            return data.data.id;
        },

        updateTags: collectionHelpers.actions.updateModelTags(),

        updateGroups: collectionHelpers.actions.updateModelGroups(),
    },

    mutations: {
        UPDATE_TAGS: collectionHelpers.mutations.UPDATE_TAGS,
        UPDATE_GROUPS: collectionHelpers.mutations.UPDATE_GROUPS,
        CLEAR_SORTING: collectionHelpers.mutations.CLEAR_SORTING,
    },
});
